/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-array-index-key */
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Tabs, Tab, Collapse } from '@material-ui/core';
import { useState, useEffect } from 'react';
import RichTextEditor from '@comp/RichTextEditor/RichTextEditor';
import Dropdown from '@comp/Dropdown/Dropdown';
import H4 from '@base/H4/H4';
import TextField from '@base/TextField/TextField';
import NumberField from '@base/NumberField/NumberField';
import P1 from '@base/P1/P1';
import Checkbox from '@base/Checkbox/Checkbox';
import H6 from '@base/H6/H6';
import H5 from '@base/H5/H5';
import {
  editOption,
  editAnswer,
  editHint,
  editLanguage,
  addOption,
  removeOption,
  editContent
} from '@actions/question';
import { useDispatchToStore } from '@hooks/reduxHooks';
import {
  Spacings,
  BgColors,
  ShadowColors,
  BorderColors,
  TextColors
} from '@styles/index';
import { QUESTION_TYPE_MAP, INTEGER_TYPE_MAP } from '@constants/quiz';

const Content = styled.div`
  padding: ${Spacings.SPACING_4B};
  background-color: ${BgColors.WHITE};
  border-radius: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_8B};
  box-shadow: 0 16px 32px ${ShadowColors.PRIMARY_SHADOW};
`;

const Editor = styled(RichTextEditor)`
  margin-top: ${Spacings.SPACING_6B};
`;

const SolutionEditor = styled(Editor)`
  margin-bottom: ${Spacings.SPACING_4B};
`;

const QuestionEditor = styled(Editor)`
  margin-bottom: ${Spacings.SPACING_10B};
`;

const Flex = styled(Content)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Input = styled(TextField)`
  width: 340px;
`;

const Actions = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_2B};
`;

const Limits = styled(H6)`
  margin-top: ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_3B};
`;

const Check = styled(Checkbox)`
  margin-top: ${Spacings.SPACING_4B};
  margin-bottom: ${({ $isLast }) => !$isLast && Spacings.SPACING_10B};
`;

const ItemTabs = styled(Tabs)`
  grid-column: 1/18;
  border-bottom: 1px solid ${BorderColors.BLUE};
  &.MuiTabs-root {
    .MuiTabs-scroller {
      .MuiTabs-indicator {
        background-color: ${BgColors.BLUE};
        height: ${Spacings.SPACING_1B};
      }
      .MuiTabs-flexContainer {
        overflow-x: scroll;
        .MuiButtonBase-root {
          .MuiTab-wrapper {
            font-weight: 600;
            font-size: 16px;
            line-height: 150%;
            text-transform: none;
          }
        }
      }
    }
  }
`;

const ItemTab = styled(Tab)`
  &.MuiTab-root {
    min-width: 110px;
  }
`;

const AddOption = styled(H5)`
  cursor: pointer;
  width: fit-content;
  :hover {
    color: ${TextColors.BLUE};
  }
`;

const LanguageSelector = styled(Dropdown)`
  width: 176px;
  margin-top: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_6B};
`;

const IsEmpty = (value) =>
  value === null || value === undefined || value === '';

const ContentEditor = ({
  data,
  selectedLanguage,
  setSelectedLanguage,
  onChange,
  handleMarkCorrect,
  handleIsMistake
}) => {
  const dispatch = useDispatchToStore();
  const {
    questionType,
    isIntegerRange,
    integerDataType,
    hasPassage,
    selectedLanguages,
    lowerLimit,
    upperLimit,
    noPreference,
    allLanguages,
    singleSolution,
    videoSolution
  } = data;
  const [currentTab, setCurrentTab] = useState(0);
  useEffect(() => {
    let newTab = currentTab;
    if (selectedLanguage >= 0 && newTab >= selectedLanguages.length)
      newTab -= 1;
    setSelectedLanguage(selectedLanguages[newTab || 0]?.id);
    setCurrentTab(newTab);
  }, [currentTab, selectedLanguages]);

  const handleIntergerPrefill = (filteredLanguages = selectedLanguages) => {
    if (questionType === QUESTION_TYPE_MAP.INTEGER) {
      const selectedLanguageObj = (filteredLanguages || []).find(
        (languageObj) => !IsEmpty(singleSolution?.[languageObj.id])
      );
      if (integerDataType === INTEGER_TYPE_MAP.ALPHA) {
        if (IsEmpty(singleSolution?.[selectedLanguage])) {
          const value = singleSolution?.[selectedLanguageObj?.id];
          dispatch(
            editContent({
              value,
              languages: filteredLanguages,
              id: 'singleSolution'
            })
          );
        }
      } else {
        const value = singleSolution?.[selectedLanguageObj?.id];
        if (IsEmpty(singleSolution?.[selectedLanguage]) && !IsEmpty(value)) {
          dispatch(
            editContent({
              value,
              languages: filteredLanguages,
              id: 'singleSolution'
            })
          );
        }

        const lowerValue = lowerLimit?.[selectedLanguageObj?.id];
        if (IsEmpty(lowerLimit?.[selectedLanguage]) && !IsEmpty(lowerValue)) {
          dispatch(
            editContent({
              value: lowerValue,
              languages: filteredLanguages,
              id: 'lowerLimit'
            })
          );
        }

        const upperValue = upperLimit?.[selectedLanguageObj?.id];
        if (IsEmpty(upperLimit?.[selectedLanguage]) && !IsEmpty(upperValue)) {
          dispatch(
            editContent({
              value: upperValue,
              languages: filteredLanguages,
              id: 'upperLimit'
            })
          );
        }
      }
    }
  };

  useEffect(() => {
    handleIntergerPrefill();
  }, [currentTab]);

  const handleTabChange = (event, value) => setCurrentTab(value);

  const handleLanguageClick = (option) => {
    if (option.id === selectedLanguage) return;
    if (selectedLanguage < 0)
      setTimeout(() => setSelectedLanguage(option.id), 300);
    const isAlreadyPresent = selectedLanguages.some(
      (language) => language.id === option.id
    );
    const filteredLanguages = selectedLanguages.filter(
      (language) => language.id !== option.id
    );
    if (isAlreadyPresent) {
      const index = filteredLanguages.findIndex(
        (language) => language.id === selectedLanguage
      );
      filteredLanguages[index] = option;
      setCurrentTab(index);
    } else filteredLanguages[currentTab] = option;
    dispatch(
      editLanguage(
        filteredLanguages,
        isAlreadyPresent ? 1 : 0,
        option.id,
        selectedLanguage
      )
    );

    handleIntergerPrefill(filteredLanguages);
  };

  const handleAnswerChange = (option, language) => (editorState) =>
    dispatch(editAnswer(editorState, language, option));

  const handleHintChange = (option, language) => (editorState) => {
    dispatch(editHint(editorState, language, option));
  };

  const handleVideoChange = (event) =>
    dispatch(editOption('videoSolution', event.target.value));

  const handeAnswerInput = (id) => (event) => {
    const { value } = event.target;
    dispatch(editContent({ value, languages: selectedLanguages, id }));
  };

  const handleAnswerIntergerInput = (id) => (_, other) => {
    dispatch(editContent({ value: other, languages: selectedLanguages, id }));
  };

  const handleShowRange = (id) => (event) => {
    const { checked } = event.target;

    dispatch(editOption(id, checked));
  };

  const addNewOption = () => dispatch(addOption());

  const removeLastOption = () => dispatch(removeOption());

  return (
    <>
      <ItemTabs
        value={currentTab ?? selectedLanguages.length - 1}
        onChange={handleTabChange}
      >
        {selectedLanguages.map(({ name, id }) => (
          <ItemTab
            label={name}
            key={`${id}lang`}
            onClick={() => {
              setSelectedLanguage(id);
            }}
          />
        ))}
        {noPreference.map(({ name, id }) => (
          <ItemTab
            label={name}
            key={`${id}np`}
            onClick={() => {
              setSelectedLanguage(id);
            }}
          />
        ))}
      </ItemTabs>
      <Content>
        <LanguageSelector
          placeholder="Select"
          options={
            selectedLanguage > 0
              ? allLanguages
              : allLanguages.filter(
                  (lang1) =>
                    !selectedLanguages.some((lang2) => lang1.id === lang2.id)
                )
          }
          menuWidth="176px"
          menuColor={TextColors.BLUE}
          onOptionClick={handleLanguageClick}
          selectedOption={allLanguages.find(
            (language) => language.id === selectedLanguage
          )}
        />
        <Collapse in={hasPassage} timeout="auto" unmountOnExit>
          <H4>Passage</H4>
          <QuestionEditor
            toolbarId="tpassage"
            placeholder="Type passage..."
            value={data.passage[selectedLanguage]}
            key={`${selectedLanguage}pass`}
            onChange={onChange('passage', selectedLanguage)}
          />
        </Collapse>
        <H4>Question</H4>
        <QuestionEditor
          toolbarId="tquestion"
          placeholder="Enter a question..."
          value={data.question[selectedLanguage]}
          key={`${selectedLanguage}que`}
          onChange={onChange('question', selectedLanguage)}
        />
        {questionType === QUESTION_TYPE_MAP.INTEGER ? (
          <>
            {integerDataType === INTEGER_TYPE_MAP.ALPHA ? (
              <TextField
                placeholder="Option choice"
                type="text"
                fullWidth
                value={singleSolution?.[selectedLanguage]}
                onChange={handeAnswerInput('singleSolution')}
              />
            ) : (
              <NumberField
                fullWidth
                allowNegative
                allowFloat
                placeholder="Option choice"
                value={singleSolution?.[selectedLanguage]}
                onChange={handleAnswerIntergerInput('singleSolution')}
              />
            )}
            <Collapse in={integerDataType === 0} timeout="auto" unmountOnExit>
              <Check
                checked={isIntegerRange}
                label="Specify a range"
                color={TextColors.BLUE}
                onChange={handleShowRange('isIntegerRange')}
                $isLast
              />
            </Collapse>
            <Collapse
              in={integerDataType === 0 && isIntegerRange}
              timeout="auto"
              unmountOnExit
            >
              <Limits>Lower limit</Limits>
              <NumberField
                fullWidth
                allowNegative
                allowFloat
                placeholder="Type here"
                value={lowerLimit?.[selectedLanguage]}
                onChange={handleAnswerIntergerInput('lowerLimit')}
              />
              <Limits>Upper limit</Limits>
              <NumberField
                fullWidth
                allowNegative
                allowFloat
                placeholder="Type here"
                value={upperLimit?.[selectedLanguage]}
                onChange={handleAnswerIntergerInput('upperLimit')}
              />
            </Collapse>
          </>
        ) : (
          <>
            {data.options.map((option, index) => (
              <>
                <Editor
                  toolbarId={`t${index}`}
                  value={option[selectedLanguage]}
                  key={`${selectedLanguage}_${index}op`}
                  onChange={handleAnswerChange(index, selectedLanguage)}
                  placeholder={`Option ${index + 1}`}
                />
                <Actions>
                  <Checkbox
                    checked={data.isCommonMistakes[index]}
                    label="Is common mistake"
                    onChange={handleIsMistake(index, dispatch)}
                    color={TextColors.BLUE}
                  />
                  <Checkbox
                    checked={data.correctAnswer.includes(index + 1)}
                    label={
                      data.correctAnswer.includes(index + 1)
                        ? 'Marked as correct'
                        : 'Mark as correct answer'
                    }
                    onChange={handleMarkCorrect(
                      index + 1,
                      questionType === QUESTION_TYPE_MAP.MCQ,
                      dispatch
                    )}
                    color={TextColors.BLUE}
                  />
                </Actions>
                <Collapse
                  in={data.isCommonMistakes[index]}
                  timeout="auto"
                  unmountOnExit
                >
                  <H6>
                    {`Mistake hint for option ${String.fromCharCode(
                      'A'.charCodeAt() + index
                    )}`}
                  </H6>
                  <Editor
                    toolbarId={`hint${selectedLanguage}hint${index}`}
                    value={data.mistakeHints[index][selectedLanguage]}
                    key={`hint${selectedLanguage}hint${index}`}
                    onChange={handleHintChange(index, selectedLanguage)}
                    placeholder="Type hint here"
                  />
                </Collapse>
              </>
            ))}
            {data.options.length === 4 && (
              <AddOption onClick={addNewOption}>Add another option</AddOption>
            )}
            {data.options.length === 5 && (
              <AddOption onClick={removeLastOption}>Remove option</AddOption>
            )}
          </>
        )}
      </Content>
      <Content>
        <H4>Solution</H4>
        <SolutionEditor
          toolbarId={`solutionExplanation${selectedLanguage}`}
          value={data.solutionExplanation[selectedLanguage]}
          onChange={onChange('solutionExplanation', selectedLanguage)}
          key={`solutionExplanation${selectedLanguage}`}
          placeholder="Type solution here"
        />
      </Content>
      <Content>
        <H4>Hint</H4>
        <SolutionEditor
          toolbarId="thint"
          value={data.hint[selectedLanguage]}
          onChange={onChange('hint', selectedLanguage)}
          key={`${selectedLanguage}hint`}
          placeholder="Type hint here"
        />
      </Content>
      <Flex>
        <P1>Video solution</P1>
        <Input
          placeholder="Enter video solution"
          value={videoSolution}
          onChange={handleVideoChange}
        />
      </Flex>
    </>
  );
};

export default ContentEditor;

ContentEditor.propTypes = {
  data: PropTypes.objectOf(PropTypes.any),
  selectedLanguage: PropTypes.number,
  setSelectedLanguage: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  handleMarkCorrect: PropTypes.func.isRequired,
  handleIsMistake: PropTypes.func.isRequired
};

ContentEditor.defaultProps = {
  data: {},
  onChange: () => {},
  selectedLanguage: -1
};
